import React, { useState, useMemo } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { cn } from "@/utils/utils";
import Text from "@/components/ui/text";
import { useTranslation } from "react-i18next";
import ConfirmationModal from "@/components/layouts/ConfirmationModal";
import { APIFieldItem } from "@/pages/settings/types";
import { getWorkflowColumnTitle } from "../FieldManagement/utils";
import {
  EntityType,
  entityTypeToEntityName
} from "@/services/hooks/entities/useEntityType";
import { QuestionnaireType } from "@/components/controls/Questionnaires/Questionnaire";
import { groupFieldsBySection } from "../StageStudio/workflow/helpers";
import { FieldRow } from "@/pages/settings/components/dealsSettings/FieldManagement/FieldRow";
import { Add, FieldsBadge } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import IconContainer from "@/components/ui/icon-container";

const TRANSLATION_KEY_TABS = "settings.deals";

interface FieldManagementTableProps {
  entityType: EntityType;
  fields: APIFieldItem[];
  onDragEnd: (result: any) => void;
  onEditField: (field: APIFieldItem) => void;
  onDeleteField: (fieldId: string) => void;
  onToggleVisibility?: (fieldId: string) => void;
  hideFieldEnabled?: boolean;
  useEnhancedDeleteConfirmation?: boolean;
  deleteConfirmationText?: string;
  deleteConfirmationTextVariant?: "body" | "bodySecondary";
  deleteCheckboxLabel?: string;
  showWorkflowColumn?: boolean;
  workflowType?: QuestionnaireType;
  disableDragAndDrop?: boolean;
  onAddField?: () => void;
  anchoredFieldKeys?: string[];
  onRowClick?: (field: APIFieldItem) => void;
  preventDeleteLastField?: boolean;
}

export const FieldManagementTable: React.FC<FieldManagementTableProps> = ({
  entityType,
  fields,
  onDragEnd,
  onEditField,
  onDeleteField,
  onToggleVisibility,
  hideFieldEnabled = false,
  useEnhancedDeleteConfirmation = false,
  deleteConfirmationText,
  deleteConfirmationTextVariant = "bodySecondary",
  deleteCheckboxLabel,
  showWorkflowColumn = false,
  workflowType = QuestionnaireType.SIMPLE,
  disableDragAndDrop = false,
  onAddField,
  anchoredFieldKeys = [],
  onRowClick,
  preventDeleteLastField = false
}) => {
  const { t, i18n } = useTranslation();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isUnlinkModalOpen, setIsUnlinkModalOpen] = useState(false);
  const [fieldToDelete, setFieldToDelete] = useState<string | null>(null);
  const [isDeleteConfirmed, setIsDeleteConfirmed] = useState(false);

  const groupedFields = useMemo(() => {
    if (workflowType === QuestionnaireType.SCORE) {
      return groupFieldsBySection(fields);
    }
    return null;
  }, [fields, workflowType]);

  // Flatten grouped fields back to a single array for drag-and-drop indexing
  const flattenedFields = useMemo(() => {
    if (groupedFields) {
      return groupedFields.flatMap((group) => group.fields);
    }
    return fields;
  }, [groupedFields]);

  const isLastField = preventDeleteLastField && fields.length === 1;

  const handleDeleteClick = (fieldId: string, isUnlink?: boolean) => {
    setFieldToDelete(fieldId);
    if (isUnlink) {
      setIsUnlinkModalOpen(true);
    } else {
      setIsDeleteConfirmed(false);
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = () => {
    if (fieldToDelete) {
      onDeleteField(fieldToDelete);
      setIsDeleteModalOpen(false);
      setFieldToDelete(null);
    }
  };

  const handleConfirmUnlink = () => {
    if (fieldToDelete) {
      onDeleteField(fieldToDelete);
      setIsUnlinkModalOpen(false);
      setFieldToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setFieldToDelete(null);
  };

  const handleCancelUnlink = () => {
    setIsUnlinkModalOpen(false);
    setFieldToDelete(null);
  };

  const SectionHeader: React.FC<{
    sectionName: string;
  }> = ({ sectionName }) => (
    <div className="flex items-center h-9 px-[6px] py-2 bg-surface-elevation2 border-b border-border-border1">
      <div className="w-6"></div>
      <div className="flex-1">
        <Text variant="smBodySecondary">
          {i18n.exists(`deals.dealView.sections.${sectionName}`)
            ? t(`deals.dealView.sections.${sectionName}`)
            : sectionName}
        </Text>
      </div>
    </div>
  );

  return (
    <div className="rounded-2xl border border-border-border1 bg-surface-widget-card overflow-hidden">
      <div className="flex items-center min-h-11 px-[6px] py-[10px] bg-surface-elevation1 border-b border-border-border1">
        <div className="w-6"></div>
        <div className="w-[234px]">
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_KEY_TABS}.fieldManagement.fieldLabel`)}
          </Text>
        </div>
        <div className="w-[200px] ml-4">
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_KEY_TABS}.fieldManagement.fieldType`)}
          </Text>
        </div>
        <div
          className={cn("ml-4", showWorkflowColumn ? "w-[150px]" : "flex-1")}
        >
          <Text variant="smBodySecondary">
            {t(`${TRANSLATION_KEY_TABS}.fieldManagement.dataSource`)}
          </Text>
        </div>
        {showWorkflowColumn && (
          <div className="flex-1 ml-4">
            <Text
              variant="smBodySecondary"
              className={
                workflowType === QuestionnaireType.SIMPLE ? "pr-5" : ""
              }
            >
              {getWorkflowColumnTitle(workflowType, t)}
            </Text>
          </div>
        )}
      </div>
      {fields.length === 0 && onAddField ? (
        <div className="flex flex-col items-center justify-center py-12 px-6">
          <div className="flex flex-col items-center gap-4">
            <FieldsBadge />
            <Text variant="bodySecondary">
              {t(
                "settings.deals.workflow.workflowDetailedView.empty.noFieldsAddedToWorkflow"
              )}
            </Text>
            <Button variant="primary" onClick={onAddField} className="gap-0">
              <IconContainer color="on-primary" className="mb-[2px]">
                <Add />
              </IconContainer>
              {t("settings.deals.workflow.workflowDetailedView.empty.addField")}
            </Button>
          </div>
        </div>
      ) : disableDragAndDrop ? (
        <div>
          {workflowType === QuestionnaireType.SCORE && groupedFields ? (
            <>
              {groupedFields.map((group) => (
                <div key={group.section}>
                  <SectionHeader sectionName={group.section} />
                  {group.fields.map((field) => {
                    const globalIndex = flattenedFields.findIndex(
                      (f) => f.id === field.id
                    );
                    return (
                      <FieldRow
                        key={field.id}
                        field={field}
                        index={globalIndex}
                        provided={null}
                        snapshot={null}
                        flattenedFields={flattenedFields}
                        showWorkflowColumn={showWorkflowColumn}
                        onEditField={onEditField}
                        handleDeleteClick={handleDeleteClick}
                        disableDragAndDrop={disableDragAndDrop}
                        isAnchored={anchoredFieldKeys.includes(field.fieldKey)}
                        onRowClick={onRowClick}
                        hideDelete={isLastField}
                        onToggleVisibility={onToggleVisibility}
                        hideFieldEnabled={hideFieldEnabled}
                      />
                    );
                  })}
                </div>
              ))}
            </>
          ) : (
            fields.map((field, index) => (
              <FieldRow
                key={field.id}
                field={field}
                index={index}
                provided={null}
                snapshot={null}
                flattenedFields={flattenedFields}
                showWorkflowColumn={showWorkflowColumn}
                onEditField={onEditField}
                handleDeleteClick={handleDeleteClick}
                disableDragAndDrop={disableDragAndDrop}
                isAnchored={anchoredFieldKeys.includes(field.fieldKey)}
                onRowClick={onRowClick}
                hideDelete={isLastField}
                onToggleVisibility={onToggleVisibility}
                hideFieldEnabled={hideFieldEnabled}
              />
            ))
          )}
        </div>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="fields-table">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {workflowType === QuestionnaireType.SCORE && groupedFields ? (
                  <>
                    {groupedFields.map((group) => (
                      <div key={group.section}>
                        <SectionHeader sectionName={group.section} />
                        {group.fields.map((field) => {
                          const globalIndex = flattenedFields.findIndex(
                            (f) => f.id === field.id
                          );
                          const isFieldAnchored = anchoredFieldKeys.includes(
                            field.fieldKey
                          );
                          return (
                            <Draggable
                              key={field.id}
                              draggableId={field.id}
                              index={globalIndex}
                              isDragDisabled={isFieldAnchored}
                            >
                              {(provided, snapshot) => (
                                <FieldRow
                                  field={field}
                                  index={globalIndex}
                                  provided={provided}
                                  snapshot={snapshot}
                                  flattenedFields={flattenedFields}
                                  showWorkflowColumn={showWorkflowColumn}
                                  onEditField={onEditField}
                                  handleDeleteClick={handleDeleteClick}
                                  disableDragAndDrop={disableDragAndDrop}
                                  isAnchored={isFieldAnchored}
                                  onRowClick={onRowClick}
                                  hideDelete={isLastField}
                                  onToggleVisibility={onToggleVisibility}
                                  hideFieldEnabled={hideFieldEnabled}
                                />
                              )}
                            </Draggable>
                          );
                        })}
                      </div>
                    ))}
                  </>
                ) : (
                  fields.map((field, index) => {
                    const isFieldAnchored = anchoredFieldKeys.includes(
                      field.fieldKey
                    );
                    return (
                      <Draggable
                        key={field.id}
                        draggableId={field.id}
                        index={index}
                        isDragDisabled={isFieldAnchored}
                      >
                        {(provided, snapshot) => (
                          <FieldRow
                            field={field}
                            index={index}
                            provided={provided}
                            snapshot={snapshot}
                            flattenedFields={flattenedFields}
                            showWorkflowColumn={showWorkflowColumn}
                            onEditField={onEditField}
                            handleDeleteClick={handleDeleteClick}
                            disableDragAndDrop={disableDragAndDrop}
                            isAnchored={isFieldAnchored}
                            onRowClick={onRowClick}
                            hideDelete={isLastField}
                            onToggleVisibility={onToggleVisibility}
                            hideFieldEnabled={hideFieldEnabled}
                          />
                        )}
                      </Draggable>
                    );
                  })
                )}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.title`)}
        confirmationText={
          useEnhancedDeleteConfirmation && deleteConfirmationText
            ? deleteConfirmationText
            : t(
                `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.confirmationText`,
                { entitiesName: entityTypeToEntityName[entityType] }
              )
        }
        confirmationTextVariant={deleteConfirmationTextVariant}
        handleSubmit={handleConfirmDelete}
        handleCancel={handleCancelDelete}
        submitButtonText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.deleteField.submitButton`
        )}
        cancelButtonText={t("cancel")}
        checkboxLabel={
          useEnhancedDeleteConfirmation ? deleteCheckboxLabel : undefined
        }
        isCheckboxRequired={useEnhancedDeleteConfirmation}
        onCheckboxChange={
          useEnhancedDeleteConfirmation ? setIsDeleteConfirmed : undefined
        }
        isCheckboxChecked={isDeleteConfirmed}
      />
      <ConfirmationModal
        isOpen={isUnlinkModalOpen}
        setIsOpen={setIsUnlinkModalOpen}
        title={t(`${TRANSLATION_KEY_TABS}.fieldManagement.unlinkField.title`)}
        confirmationText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.unlinkField.confirmationText`,
          { childObjectName: entityTypeToEntityName[entityType] }
        )}
        handleSubmit={handleConfirmUnlink}
        handleCancel={handleCancelUnlink}
        submitButtonText={t(
          `${TRANSLATION_KEY_TABS}.fieldManagement.unlinkField.submitButton`
        )}
        cancelButtonText={t("cancel")}
        isPrimaryButton
        hideDivider
        headerClassname="border-none"
      />
    </div>
  );
};
